import { useCallback, useEffect, useRef, type RefObject } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { cancelExport, listExports, markExportsSeen } from '../api/exports.api'
import type { ExportJob, ExportScope } from '../api/contracts'
import { exportScopeKey, exportsQueryKeys } from '../api/queryKeys'
import { exportPollMs, isExportActive } from '../logic/exportJobs'
import { addExport, markSeen, syncExports, withKnownExports } from '../logic/exportJobCache'
import { useExportMenusStore } from '../stores/useExportMenusStore'
import { useExportWatchStore } from '../stores/useExportWatchStore'

/** Every table's and list's recent exports, and the header's, so a change shows everywhere. */
const ALL_LISTS = ['exports', 'list'] as const

/** The person's recent exports of a table, a list, or all of them, read every
 * couple of seconds while one is waiting or running; read again whenever `open`
 * (the menu) turns true, so it's current without polling an idle list. */
export function useRecentExports(scope: ExportScope | null, { open }: { open: boolean }) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: exportsQueryKeys.list(scope ? exportScopeKey(scope) : null),
    // Each export as far along as any list has seen it, and moved forward in
    // every other list too, so every menu's dot agrees (logic/exportJobCache).
    queryFn: async ({ signal }) => {
      const data = await listExports(scope!, signal)
      const exports = withKnownExports(queryClient, data.exports)
      syncExports(queryClient, exports)
      return { ...data, exports }
    },
    enabled: scope !== null,
    select: (data) => data.exports,
    refetchInterval: (query) => {
      const active = (query.state.data?.exports ?? []).filter(isExportActive)
      return active.length > 0 ? exportPollMs(active) : false
    },
  })
  const { refetch } = query
  useEffect(() => {
    if (open && scope) void refetch()
  }, [open, refetch, scope])
  return query
}

/**
 * Opening a menu marks its finished exports seen (on the server, so every tab
 * and device agrees), and its button loses its blue dot.
 */
export function useMarkSeenOnOpen(scope: ExportScope | null, open: boolean, jobs: ExportJob[]) {
  const queryClient = useQueryClient()
  const unseen = jobs.some((job) => job.status === 'done' && !job.seen_at)
  useEffect(() => {
    if (!open || !scope || !unseen) return
    // Seen in every list at once: the header's dot and the table's clear together.
    const listed = queryClient.getQueryData<{ exports: ExportJob[] }>(
      exportsQueryKeys.list(exportScopeKey(scope))
    )
    const ids = (listed?.exports ?? [])
      .filter((job) => job.status === 'done' && !job.seen_at)
      .map((job) => job.export_id)
    markSeen(queryClient, ids, new Date().toISOString())
    void markExportsSeen(scope)
      .then(() => queryClient.invalidateQueries({ queryKey: ALL_LISTS }))
      .catch(() => undefined)
  }, [open, queryClient, scope, unseen])
}

/**
 * Lists this menu among the Export menus Open When Ready can open: `open`
 * opens it without taking focus; `buttonRef` tells whether it's on screen.
 */
export function useRegisterExportMenu(
  scope: ExportScope | null,
  buttonRef: RefObject<HTMLElement | null>,
  open: () => void
) {
  const openRef = useRef(open)
  openRef.current = open
  const scopeKey = scope ? exportScopeKey(scope) : null
  useEffect(() => {
    if (!scopeKey) return
    return useExportMenusStore.getState().register(scopeKey, {
      open: () => openRef.current(),
      element: () => buttonRef.current,
    })
  }, [buttonRef, scopeKey])
}

/**
 * Lists this grid's Export menu for Ctrl+P: `print` prints its PDF as the
 * menu's print button does; `buttonRef` tells whether it's on screen.
 */
export function useRegisterExportPrinter(
  enabled: boolean,
  buttonRef: RefObject<HTMLElement | null>,
  print: () => void
) {
  const printRef = useRef(print)
  printRef.current = print
  useEffect(() => {
    if (!enabled) return
    return useExportMenusStore.getState().registerPrinter({
      print: () => printRef.current(),
      element: () => buttonRef.current,
    })
  }, [buttonRef, enabled])
}

/**
 * Queue an export (`queue` posts it) and wait on it (ExportWatcher says when
 * it's ready). With `print`, ExportWatcher opens the print dialog for it
 * instead; with `share`, the device's share sheet.
 */
export function useStartExport() {
  const queryClient = useQueryClient()
  const location = useLocation()
  const watch = useExportWatchStore((state) => state.watch)

  return useCallback(
    async (
      scope: ExportScope,
      queue: () => Promise<ExportJob>,
      { print = false, share = false }: { print?: boolean; share?: boolean } = {}
    ): Promise<ExportJob> => {
      const scopeKey = exportScopeKey(scope)
      const { begin, end, beginPrint, endPrint, beginShare, endShare } =
        useExportWatchStore.getState()
      begin(scopeKey)
      if (print) beginPrint(scopeKey)
      if (share) beginShare(scopeKey)
      let job: ExportJob
      try {
        job = await queue()
      } catch (error) {
        end(scopeKey)
        if (print) endPrint(scopeKey)
        if (share) endShare(scopeKey)
        throw error
      }
      // A small one comes back built: the watcher has it without asking again.
      queryClient.setQueryData(exportsQueryKeys.job(job.export_id), job)
      watch({
        exportId: job.export_id,
        scopeKey,
        fileName: job.file_name,
        startedOnPath: location.pathname,
        ...(print ? { print } : {}),
        ...(share ? { share } : {}),
      })
      // Its list and the header's have it now, so both dots stay amber without a gap.
      addExport(queryClient, scopeKey, job)
      end(scopeKey)
      void queryClient.invalidateQueries({ queryKey: ALL_LISTS })
      return job
    },
    [location.pathname, queryClient, watch]
  )
}

/** Cancel a waiting or running export, or remove a finished one. */
export function useCancelExport() {
  const queryClient = useQueryClient()
  return useCallback(
    async (exportId: string) => {
      await cancelExport(exportId)
      useExportWatchStore.getState().finish(exportId)
      // Off every list at once: a canceled export isn't shown.
      queryClient.setQueriesData<{ exports: ExportJob[] }>({ queryKey: ALL_LISTS }, (current) =>
        current ? { exports: current.exports.filter((job) => job.export_id !== exportId) } : current
      )
      await queryClient.invalidateQueries({ queryKey: ALL_LISTS })
    },
    [queryClient]
  )
}
